import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import {
  Container,
  Snackbar,
  Alert,
  Icon,
  useMediaQuery,
  useTheme
} from "@mui/material";

import {
  completeRepairRequests,
  getBuyerContactInfo,
  getRepairRequestStatus,
  getRepairRequests,
  updateResponsiblePersonForRepairRequests,
} from "../../../apis/aftercareApis";
import {
  getAfterCareRepairRequestVisibleColumns,
  getModifiedResponse,
  getAfterCareRepairRequestHiddenColumns ,
  ADDITIONAL_FIELDS_REQUIRED_IN_PRINT_AND_DOWNLOAD,
  isHiddenResponsibleMenuItem,
} from "../../../Pages/Aftercare/Grid/util";

import useGridDataModalHandler from "../../../_hooks/useGridDataModalHandler";
import {
  getOrganizations,
  getRepairRequestCarryOutAsTypeList,
  getRepairRequestLocations,
} from "../../../apis/surveyingApis";
import MobileCustomRow from "../../../_hooks/MobileCustomRow";
import DataGridV2 from "../../../components/DataGridV2";
import {
  groupBy,
  history,
  setLocalStorageItem,
  formatDate,
  getLocalStorageItem,
  repairRequestDbStatuses
} from "../../../_helpers";
import { apps, LOCAL_STORAGE_GRID_KEYS, userAccountRoleConstants } from "../../../_constants";
import AddRepairRequest from "../AddRepairRequest";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import SelectResponsiblePerson from "../../Survey/components/SelectResponsiblePerson";
import { Add, Chat, CheckCircleOutlineSharp, WatchLaterSharp } from "@mui/icons-material";
import { flushSync } from "react-dom";
import { INITIAL_ROW_SELECTION_MODEL } from "../../../components/DataGridV2/constants";
import { deepClone } from "../../../_helpers/deepClone";
import BuyerContactPopover from "../../../components/BuyerContactPopover";
import RepairRequestCompleteTaskPopover from "../../../components/RepairRequestCompleteTaskPopover";
import { getIdsForGridSelectedRows } from "../../../_helpers/getIdsForGridSelectedRows";

const updateLocationIconVisibility = (keyPrefix) => {
  let localColumnVisibility = getLocalStorageItem(LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_VISIBILITY[keyPrefix]) || {};
  if (!localColumnVisibility.location) {
    return;
  }
  const updatedVisibility = { ...localColumnVisibility };

  // If 'location' is true or false, adjust 'locationIcon' visibility
  if (localColumnVisibility.hasOwnProperty('location') && !localColumnVisibility.hasOwnProperty('locationIcon')) {
    updatedVisibility.locationIcon = localColumnVisibility.location;
    setLocalStorageItem(LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_VISIBILITY[keyPrefix], updatedVisibility);
  }
};

// FIXME: rename this file, move to common place and accodrdingly update routes for aftercare and construction quality
export const AfterCareRepairRequests = (props) => {
  const { t, i18n, ready } = useTranslation();
  const theme= useTheme()
  const classes = useStyles();
  const [isOpenObjectModal, setIsOpenObjectModal] = useState(null);
  const [locations, setLocations] = useState([]);
  const [optionsDataLoading, setOptionsDataLoading] = useState({
    location: false,
    carryOutAsType: false,
    statusList: false,
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [carryOutAsTypeList, setCarryOutAsTypeList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const apiRef = useGridApiRef();
  const [openExportModal, setOpenExportModal] = useState(false);
  const muiTableRef = useRef();
  const isPageLoad = useRef(true)
  const [isSubmitting, setIsSubmitting] = useState(true);
  const [responsiblePopupPosition, setResponsiblePopupPosition] = useState({ x: 0, y: 0 })
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [anchorElCompleteTaskInfo, setAnchorElCompleteTaskInfo] = useState(null);
  const [completionText,setCompletionText] = useState();
  const [notes,setNotes] = useState();
  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "error"
  })
  const [buyerContactInfo, setBuyerContactInfo] = useState({
    loading: false,
    data: null,
    anchorEl: null,
  });
  const [allBuyerContacts, setAllBuyerContacts] = useState({});
  const app = useSelector((state) => state.app);
  const { lng } = useSelector((state) => state.appConfig) || {}
  const { all: buildings } = useSelector((state) => state.buildings);
  const handleExportModal = useCallback(() => { setOpenExportModal(!openExportModal) }, [openExportModal])
  const rrDbStatuses = useMemo(() => repairRequestDbStatuses(i18n, lng), [lng, i18n?.isInitialized, ready]);

  const keyPrefix = app === apps.aftercare ? "AFTERCARE" :  app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
  const isConstructionQualityApp = app === apps.constructionQuality

  updateLocationIconVisibility(keyPrefix);

  const resetResponsiblePopupPosition = () => {
    setResponsiblePopupPosition({
      x: 0,
      y: 0
    })
  }

  const handleResponsiblePerson = async (data) => {
    try {
      const response = await updateResponsiblePersonForRepairRequests(app, data.loginId,Array.from(rowSelectionModel?.ids))
      if (response.status === 200) {
        setToast({
          open: true,
          message: t("repairRequest.updateResponsiblePerson.success"),
          type: "success"
        });
      } else {
        setToast({
          open: true,
          message: t("general.failedToUpdate"),
          type: "error"
        });
      }
      resetResponsiblePopupPosition()
      setIsSubmitting(false)
    } catch (error) {
      setToast({
        open: true,
        message: t("general.failedToUpdate"),
        type: "error"
      });
      resetResponsiblePopupPosition()
      setIsSubmitting(false)
    }
  }

  const handleOpenBuyerContactInfo = async (event, rrId) => {
    event.stopPropagation();
    const anchorEl = event.currentTarget;
    if (allBuyerContacts?.[rrId]) {
      setBuyerContactInfo({
        loading: false,
        data: allBuyerContacts[rrId],
        anchorEl: anchorEl,
      });
      return;
    }

    setBuyerContactInfo({
      loading: true,
      data: null,
      anchorEl: anchorEl,
    });

    try {
      const response = await getBuyerContactInfo(rrId);
      const buyerContactDetails = response.data
      setAllBuyerContacts({
        ...allBuyerContacts,
        [rrId]: buyerContactDetails,
      });
      setBuyerContactInfo({
        anchorEl: anchorEl,
        data: buyerContactDetails,
        loading: false,
      });
    } catch (error) {
      console.error(error);
      setBuyerContactInfo({
        anchorEl: anchorEl,
        loading: false,
        data:null
      });
    }
  };

  const handleCloseBuyerContactInfo = () => {
    setBuyerContactInfo({
      loading: false,
      data: null,
      anchorEl: null,
    });
  };

  const {
    loading,
    loadedRows,
    sortModel,
    handleSort,
    handleCellSort,
    filterModel,
    handleFilter,
    resetData,
    handleSearch,
    searchText,
    getAllRecords,
    handleCellFilter,
    totalRecords,
    paginationModel,
    onPaginationModelChange,
    getAllExportRecords
  } = useGridDataModalHandler({
    modifyResponse: getModifiedResponse(buildings, t),
    additionalParams: { app },
    rowIdentifier : "requestId",
    apiCall: getRepairRequests,
    sortModal: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_SORT[keyPrefix], undefined
    ),
    filterModal: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix], undefined
    ),
    paginationModel: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_PAGINATION[keyPrefix], undefined
    ),
    searchText: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_SEARCH_TEXT[keyPrefix], ""
    ),
    dateTimeFields: ["createdOn" , "modifiedOn"]
  });


  const selectedRows = loadedRows.filter(row => rowSelectionModel?.type === "exclude"  ? !Array.from(rowSelectionModel?.ids).includes(row.requestId) :Array.from(rowSelectionModel?.ids).includes(row.requestId));
  const isRepairRequestsCompleted= selectedRows.some(row => row?.completed);
  const projectIds = useMemo(
    () => groupBy(buildings, "projectId") || {},
    [buildings]
  );


  useEffect(() => {
    getFilterOptions();
  }, []);

  useEffect(() => {
    // handle filter applied from dashboard
    const locationState = props?.location?.state
    const statusNames = locationState?.status ?? [];
    const newFilterModelItems = [];
    if (locationState?.selectedProject?.projectId) {
      newFilterModelItems.push(
        {
          field: "projectId",
          operator: "is",
          value: locationState.selectedProject.projectId
        },
      )
    }
    if (locationState?.selectedProject?.buildingNoIntern && locationState.filter === "buildingNoIntern" ) {
      newFilterModelItems.push(
        {
          field: "buildingNoIntern",
          operator: "equals",
          value: locationState.selectedProject.buildingNoIntern
        },
      )
    }
    if(statusNames?.length > 0){
      newFilterModelItems.push(
        {
          field: "status",
          operator: "isAnyOf",
          value: statusNames
        },
      )
    }
    if(locationState?.isOverDue){
      newFilterModelItems.push(
        {
          field: "overdue",
          operator: "is",
          value: "true"
        },
      )
    }

    // if user directly visit page, apply old filterModel else override old filter with new filter model(newFilterModel)
    handleFilter(newFilterModelItems.length > 0 ? {
      items: newFilterModelItems,
      logicOperator: "and",
      quickFilterValues: [],
      quickFilterLogicOperator: "and",
    } : filterModel ?? {
      "items": [],
      "logicOperator": "and",
      "quickFilterValues": [],
      "quickFilterLogicOperator": "and"
    })
  }, [props?.location?.state])

    useEffect(() => {
    if (Object.keys(projectIds)?.length > 0) {
      getOrganizations(Object.keys(projectIds))
        .then(({ data }) => {
          setOrganizations(
            data.map((singleOrganization) => {
              return {
                label: singleOrganization.name,
                value: singleOrganization.id,
              };
            })
          );
        })
        .catch((error) => {
          console.log({ error });
        });
    }
  }, [projectIds]);

  const isSiteManagerOrAfterCareEmployeeForBuildings = useMemo(() => {
    let count = 0
    buildings.forEach((building) => {
      if (building.roles && (building.roles.includes(userAccountRoleConstants.aftercareEmployee) || building.roles.includes(userAccountRoleConstants.siteManager))) {
        count++;
      }
    })

    return {
      ALL: count === buildings.length,
      NO_ONE: count === 0,
      MIX: count > 0 && count !== buildings.length
    }
  }, [buildings])

  const getFilterOptions = async () => {
    try {
      setOptionsDataLoading({
        location: true,
        carryOutAsType: true,
        statusList: true,
      });

      const [locationRes, typeListRes, requestStatusRes] = await Promise.all([
        getRepairRequestLocations(),
        getRepairRequestCarryOutAsTypeList(),
        getRepairRequestStatus(),
      ]);

      const locations = locationRes.data.map((x) => {
        return { label: x.name, value: x.name };
      });
      const typeList = typeListRes.data.map((x) => {
        return { label: x.name, value: x.name };
      });
      const status = requestStatusRes.data.map((x) => {
        return { label: x.name, value: x.name , closed: x.closed};
      });

      setLocations(locations);
      setCarryOutAsTypeList(typeList);
      setStatusList(status);
    } catch (e) {
      console.log(e);
    } finally {
      setOptionsDataLoading({
        location: false,
        carryOutAsType: false,
        statusList: false,
      });
    }
  };

  const listToMapRedirectHandler = useCallback(
    async (_, selectedRequest) => {
      history.push({
        pathname: isConstructionQualityApp ? "kwaliteitsborging/meldingen/map" :"/meldingen/map",
        state: {
          selectedRequest,
          requests: loadedRows,
          buildingId: selectedRequest.buildingId,
        },
      });
    },
    [loadedRows]
  );

  // NOTE: Whenever we change key name of column, then this must be update.
  const mappingOfOldFieldToNewField = {
    locationId: "location",
    statusId: "status",
    attachments: "firstFileId",
    carryOutAsTypeId: "carryOutAsType",
  }

  const columns = useMemo(() => {
    const projectNames = Object.keys(projectIds).map((projectId) => ({
      value: projectId,
      label: projectIds?.[projectId]?.[0]?.projectName,
    })).sort((a, b) => (a.label < b.label ? -1 : 1))

    const columns = getAfterCareRepairRequestVisibleColumns({
      t,
      i18n,
      listToMapRedirectHandler,
      projectNames,
      locations,
      optionsDataLoading,
      carryOutAsTypeList,
      statusList,
      organizations,
      classes,
      handleCellFilter,
      apiRef,
      handleCellSort,
      isSiteManagerOrAfterCareEmployeeForBuildings,
      isAfterCareApp :  app === apps.aftercare,
      keyPrefix,
      handleOpenBuyerContactInfo
    });
    return [...columns , ...getAfterCareRepairRequestHiddenColumns(t , !isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE)];

  }, [projectIds, isSiteManagerOrAfterCareEmployeeForBuildings, apiRef, sortModel, t, i18n, listToMapRedirectHandler, locations, optionsDataLoading, carryOutAsTypeList, statusList, organizations, classes, handleCellFilter]);

  const handleCheckboxChange = useCallback((itemId) => {
    setRowSelectionModel((prevCheckedItems) => {
      const prevIds = Array.from(prevCheckedItems?.ids);
        let newIds = [];
      if (prevIds.includes(itemId)) {
          newIds = prevIds.filter((id) => id !== itemId);
        } else {
          newIds = [...prevIds, itemId];
      }
       return {...prevCheckedItems,ids: new Set(newIds)};
    });
  }, []);

  const handleTooLateFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);
    if (cloneFilterModal?.items?.length > 0) {
      const tooLateFilterIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "overdue"
      );
      if (tooLateFilterIndex !== -1 && cloneFilterModal?.items[tooLateFilterIndex].value) {
        if (cloneFilterModal?.items[tooLateFilterIndex].value === "true") {
          const newFilterItems = cloneFilterModal?.items?.filter(
            (item) => item.field !== "overdue"
          );
          cloneFilterModal = {
            ...cloneFilterModal,
            items: newFilterItems,
          };
        } else {
          cloneFilterModal.items[tooLateFilterIndex].value = "true"
        }

      } else {
        cloneFilterModal.items.push({
          field: "overdue",
          operator: "is",
          value: "true",
        });
      }
    } else {
      cloneFilterModal = {
        items: [
          {
            field: "overdue",
            operator: "is",
            value: "true",
          },
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }

    handleFilter(cloneFilterModal, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, handleFilter]);

  const handleAttentionFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);
    if (cloneFilterModal && cloneFilterModal.items?.length) {
      const attentionFilterIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "isRequiredHandling"
      );
      if (attentionFilterIndex !== -1 && cloneFilterModal?.items[attentionFilterIndex].value) {
        if (cloneFilterModal?.items[attentionFilterIndex].value === "true") {
          const newFilterItems = cloneFilterModal?.items?.filter(
            (item) => item.field !== "isRequiredHandling"
          );
          cloneFilterModal = {
            ...cloneFilterModal,
            items: newFilterItems,
          };
        } else {
          cloneFilterModal.items[attentionFilterIndex].value = "true"
        }

      } else {
        cloneFilterModal.items.push({
          field: "isRequiredHandling",
          operator: "is",
          value: "true",
        });
      }
    } else {
      cloneFilterModal = {
        items: [
          {
            field: "isRequiredHandling",
            operator: "is",
            value: "true",
          },
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }

    handleFilter(cloneFilterModal, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, handleFilter]);

  const handleUnreadMessageFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);
    if (cloneFilterModal && cloneFilterModal.items?.length) {
      const unreadMessageFilterIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "unreadMessagesCount"
      );
      if (unreadMessageFilterIndex !== -1 && cloneFilterModal?.items[unreadMessageFilterIndex].value) {
        if (cloneFilterModal?.items[unreadMessageFilterIndex].value === "0") {
          const newFilterItems = cloneFilterModal?.items?.filter(
            (item) => item.field !== "unreadMessagesCount"
          );
          cloneFilterModal = {
            ...cloneFilterModal,
            items: newFilterItems,
          };
        } else {
          cloneFilterModal.items[unreadMessageFilterIndex].value = "0"
          cloneFilterModal.items[unreadMessageFilterIndex].operator = ">"
        }

      } else {
        cloneFilterModal.items.push({
          field: "unreadMessagesCount",
          operator: ">",
          value: "0",
        });
      }
    } else {
      cloneFilterModal = {
        items: [
          {
            field: "unreadMessagesCount",
            operator: ">",
            value: "0",
          }
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }

    handleFilter(cloneFilterModal, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, handleFilter,keyPrefix]);

  const handleNewRequestFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);

    // step1 filter is available not undefined
    if (cloneFilterModal?.items?.length > 0) {
      //step3 check status is available or not in filter
      const isStatusFilterAwaialableIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "status"
      );
      const isStatusFilterAwaialable =
        cloneFilterModal?.items?.[isStatusFilterAwaialableIndex];
      // step4 if available
      if (isStatusFilterAwaialable && isStatusFilterAwaialable.value) {
        // step6 check new status is available or not
        const isNewStatusPresent = Array.isArray(isStatusFilterAwaialable.value)
          ? isStatusFilterAwaialable.value.includes(rrDbStatuses.NEW)
          : isStatusFilterAwaialable.value === rrDbStatuses.NEW;

        // step7 if new status available
        if (isNewStatusPresent) {
          // step 10 check value is array or not, if array then check lenght is > 2 then remove from it else filter that status
          if (
            Array.isArray(isStatusFilterAwaialable.value) &&
            isStatusFilterAwaialable.value.length > 1
          ) {
            const values = isStatusFilterAwaialable.value.filter(
              (val) => val !== rrDbStatuses.NEW
            );
            cloneFilterModal.items[isStatusFilterAwaialableIndex].value =
              values;
          } else {
            // step 11 if stystus length 1 or only value then
            const newFilterItems = cloneFilterModal?.items?.filter(
              (item) => item.field !== "status"
            );
            cloneFilterModal = {
              ...cloneFilterModal,
              items: newFilterItems,
            };
          }
        } else {
          // step8 if new status not available
          if (
            Array.isArray(
              cloneFilterModal.items[isStatusFilterAwaialableIndex].value
            )
          ) {
            cloneFilterModal.items[isStatusFilterAwaialableIndex].value.push(
              rrDbStatuses.NEW
            );
          } else {
            cloneFilterModal.items[isStatusFilterAwaialableIndex] = {
              field: "status",
              operator: "isAnyOf",
              value: [
                rrDbStatuses.NEW,
                cloneFilterModal.items[isStatusFilterAwaialableIndex].value,
              ],
            };
          }
        }
      } else {
        // step5 if not available then add that
        if (isStatusFilterAwaialable) {
          cloneFilterModal.items[isStatusFilterAwaialableIndex] = {
            field: "status",
            operator: "is",
            value: rrDbStatuses.NEW,
          };
        } else {
          cloneFilterModal.items.push({
            field: "status",
            operator: "is",
            value: rrDbStatuses.NEW,
          });
        }
      }
    } else {
      // step2 if not present then add it
      cloneFilterModal = {
        items: [
          {
            field: "status",
            operator: "is",
            value: rrDbStatuses.NEW,
          },
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }
    handleFilter({ ...cloneFilterModal }, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, statusList, handleFilter]);

  const handleInProgressRequestFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);

    // step1 filter is available not undefined
    if (cloneFilterModal?.items?.length > 0) {
      //step3 check status is available or not in filter
      const isStatusFilterAwaialableIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "status"
      );
      const isStatusFilterAwaialable =
        cloneFilterModal?.items?.[isStatusFilterAwaialableIndex];
      // step4 if available
      if (isStatusFilterAwaialable && isStatusFilterAwaialable.value) {
        // step6 check new status is available or not
        const isInProgresstatusPresent = Array.isArray(
          isStatusFilterAwaialable.value
        )
          ? isStatusFilterAwaialable.value.includes(rrDbStatuses.IN_PROGRESS)
          : isStatusFilterAwaialable.value === rrDbStatuses.IN_PROGRESS;

        // step7 if new status available
        if (isInProgresstatusPresent) {
          // step 9 check value is array or not, if array then check lenght is > 2 then remove from it else filter that status
          if (
            Array.isArray(isStatusFilterAwaialable.value) &&
            isStatusFilterAwaialable.value.length > 1
          ) {
            const values = isStatusFilterAwaialable.value.filter(
              (val) => val !== rrDbStatuses.IN_PROGRESS
            );
            cloneFilterModal.items[isStatusFilterAwaialableIndex].value =
              values;
          } else {
            // step 10 if stystus length 1 or only value then
            const newFilterItems = cloneFilterModal?.items?.filter(
              (item) => item.field !== "status"
            );
            cloneFilterModal = {
              ...cloneFilterModal,
              items: newFilterItems,
            };
          }
        } else {
          // step8 if new status not available
          if (
            Array.isArray(
              cloneFilterModal.items[isStatusFilterAwaialableIndex].value
            )
          ) {
            cloneFilterModal.items[isStatusFilterAwaialableIndex].value.push(
              rrDbStatuses.IN_PROGRESS
            );
          } else {
            cloneFilterModal.items[isStatusFilterAwaialableIndex] = {
              field: "status",
              operator: "isAnyOf",
              value: [
                rrDbStatuses.IN_PROGRESS,
                cloneFilterModal.items[isStatusFilterAwaialableIndex].value,
              ],
            };
          }
        }
      } else {
        // step5 if not available then add that
        if (isStatusFilterAwaialable) {
          cloneFilterModal.items[isStatusFilterAwaialableIndex] = {
            field: "status",
            operator: "is",
            value: rrDbStatuses.IN_PROGRESS,
          };
        } else {
          cloneFilterModal.items.push({
            field: "status",
            operator: "is",
            value: rrDbStatuses.IN_PROGRESS,
          });
        }
      }
    } else {
      // step2 if not present then add it
      cloneFilterModal = {
        items: [
          {
            field: "status",
            operator: "is",
            value: rrDbStatuses.IN_PROGRESS,
          },
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }
    handleFilter({ ...cloneFilterModal }, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, statusList, handleFilter]);

  const handleCompletedRequestFilter = useCallback(() => {
    let cloneFilterModal = deepClone(filterModel);

    // derive completed statuses from statusList
    const closedStatuses = statusList
      ? statusList.filter((st) => st.closed).map((st) => st.value)
      : [];
    
    if (closedStatuses.length === 0) {
      // nothing to filter — just return or optionally clear the status filter
      return;
    }

    if (cloneFilterModal?.items?.length > 0) {
      const statusFilterIndex = cloneFilterModal.items.findIndex(
        (item) => item.field === "status"
      );
      const statusFilterItem = cloneFilterModal.items[statusFilterIndex];

      if (statusFilterItem && statusFilterItem.value) {
        // Check if completed statuses” are already part of the filter
        const isClosedStatusPresent = Array.isArray(statusFilterItem.value) && statusFilterItem.operator === "isAnyOf"
          ? closedStatuses.every((s) => statusFilterItem.value.includes(s))
          : false;

        if (isClosedStatusPresent) {
          cloneFilterModal.items[statusFilterIndex] = {
            field: "status",
            operator: "isAnyOf",
            value: cloneFilterModal.items[statusFilterIndex].value.filter((val) => !closedStatuses.includes(val)),
          };

        } else {
          // Otherwise, set the status filter to “isAnyOf completed status
          cloneFilterModal.items[statusFilterIndex] = {
            field: "status",
            operator: "isAnyOf",
            value: closedStatuses,
          };
        }
      } else {
        // status filter exists but no value, or something weird — just replace it
        if (statusFilterIndex >= 0) {
          cloneFilterModal.items[statusFilterIndex] = {
            field: "status",
            operator: "isAnyOf",
            value: closedStatuses,
          };
        } else {
          cloneFilterModal.items.push({
            field: "status",
            operator: "isAnyOf",
            value: closedStatuses,
          });
        }
      }
    } else {
      // no filters currently — set up a new filter model with completed statuses
      cloneFilterModal = {
        items: [
          {
            field: "status",
            operator: "isAnyOf",
            value: closedStatuses,
          },
        ],
        logicOperator: "and",
        quickFilterValues: [],
        quickFilterLogicOperator: "and",
      };
    }

    handleFilter({ ...cloneFilterModal }, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  }, [filterModel, statusList, handleFilter]);

    const handleOpenRequestFilter = useCallback(() => {
        let cloneFilterModal = deepClone(filterModel);
    
        // derive open statuses from statusList
        const openStatuses = statusList
          ? statusList.filter((st) => !st.closed).map((st) => st.value)
          : [];
    
        if (openStatuses.length === 0) {
          // nothing to filter — just return or optionally clear the status filter
          return;
        }
    
        if (cloneFilterModal?.items?.length > 0) {
          const statusFilterIndex = cloneFilterModal.items.findIndex(
            (item) => item.field === "status"
          );
          const statusFilterItem = cloneFilterModal.items[statusFilterIndex];
    
          if (statusFilterItem && statusFilterItem.value) {
            // Check if “open statuses” are already part of the filter
            const isOpenStatusPresent = Array.isArray(statusFilterItem.value) && statusFilterItem.operator === "isAnyOf"
              ? openStatuses.every((s) => statusFilterItem.value.includes(s))
              : false;
    
            if (isOpenStatusPresent) {
              cloneFilterModal.items[statusFilterIndex] = {
                field: "status",
                operator: "isAnyOf",
                value: cloneFilterModal.items[statusFilterIndex].value.filter((val) => !openStatuses.includes(val)),
              };
    
            } else {
              // Otherwise, set the status filter to “isAnyOf openStatuses”
              cloneFilterModal.items[statusFilterIndex] = {
                field: "status",
                operator: "isAnyOf",
                value: openStatuses,
              };
            }
          } else {
            // status filter exists but no value, or something weird — just replace it
            if (statusFilterIndex >= 0) {
              cloneFilterModal.items[statusFilterIndex] = {
                field: "status",
                operator: "isAnyOf",
                value: openStatuses,
              };
            } else {
              cloneFilterModal.items.push({
                field: "status",
                operator: "isAnyOf",
                value: openStatuses,
              });
            }
          }
        } else {
          // no filters currently — set up a new filter model with open statuses
          cloneFilterModal = {
            items: [
              {
                field: "status",
                operator: "isAnyOf",
                value: openStatuses,
              },
            ],
            logicOperator: "and",
            quickFilterValues: [],
            quickFilterLogicOperator: "and",
          };
        }
    
        handleFilter({ ...cloneFilterModal }, (newFilter) => {
          setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
            newFilter
          );
        });
      }, [filterModel, statusList, handleFilter,]);

    const getRepairRequestsForPrintAndDownload = async ({ selectedColumnsNames = [], isAllSelected = true , idsToExport = []}) => {
    let hiddenFields = getAfterCareRepairRequestHiddenColumns(t, !isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE)
    let exportRistrictedField = [...hiddenFields.map((f) => f.field), "priority", "targetCompletionDate", "overdue"]
    const allColumns = [...columns, ...ADDITIONAL_FIELDS_REQUIRED_IN_PRINT_AND_DOWNLOAD, ...hiddenFields];
    const columnNames = allColumns.reduce(
      (p, c) => ({ ...p, [c.field]: c.field }),
      {}
    );

    const {
      data: { results },
    } = await (selectedColumnsNames.length > 0 ? getAllExportRecords({
      exportColumns: selectedColumnsNames.concat(ADDITIONAL_FIELDS_REQUIRED_IN_PRINT_AND_DOWNLOAD.map((f) => f.field)),
      additionalCondition: isAllSelected ? [] : [{
        field: "requestId",
        operator: "isAnyOf",
        value: [
          ...(idsToExport)
        ],
      }]
    }) : getAllRecords());
    const modifiedData = getModifiedResponse(buildings, t)(results);
    const data = modifiedData?.map((p) => {
      return Object.keys(columnNames).reduce((prev, key) => {
        const obj = { ...prev };
        const isDateField = !!allColumns.find(x => x.field === key && x.type === 'date');
        const isDateTimeField = !!allColumns.find(x => x.field === key && x.type === 'dateTime');
        if (key === "requestId") obj["id"] = p["requestId"];
        if (key === "isRequiredHandling") {
          const array = [];
          if (p.isRequiredHandling) {
            array.push({ icon: "PriorityHigh" });
          }
          obj[key] = array;
        }
        else if (exportRistrictedField.includes(key)) {
          if (p.isAfterCareEmployeeOrSiteManager) {
            if (key === "overdue") {
              const array = [];
              if (p.overdue) array.push({ icon: "Schedule", color: "error" });
              else if (p.is48HoursReminder)
                array.push({ icon: "Schedule", color: "warning" });
              obj[key] = array;
            } else if (key === "reporterRole") {
              obj[key] = t(`db.buildingManagerRole.${p[key]}`)
            } else if (key === "receivedVia") {
              obj[key] = t(`repairRequest.receivedvia.${p[key]}`)
            } else {
              obj[key] = isDateField || isDateTimeField ? p[key] && formatDate(new Date(p[key]), isDateTimeField) : p[key]
            }
          }
        }
        else if (isDateField || isDateTimeField)
          obj[key] = p[key] && formatDate(new Date(p[key]), isDateTimeField);
        else if (key === "resolvers")
          obj[key] = p["resolvers"]
            ? [...new Set(p["resolvers"].map((a) => a.name))]
            : [];
        else if (key === "projectId") obj[key] = p["projectName"];
        else obj[key] = p[key];
        return obj;
      }, {});
    });
    return data;
  };
  const resolverIds =  useMemo(() => {
      return getIdsForGridSelectedRows(rowSelectionModel,loadedRows)
     },[rowSelectionModel, loadedRows]);

  const getToolbarProps = useMemo(() => {
    const isTooLate = filterModel?.items?.find(
      (item) => item.field === "overdue"
    )?.value === "true";


    const status = filterModel?.items?.find(
      (item) => item.field === "status"
    )?.value;

    const isNewFilterAvailable = status
      ? Array.isArray(status)
        ? status.includes(rrDbStatuses.NEW)
        : rrDbStatuses.NEW === status
      : undefined;

    const isAttentionFilterAvailable = filterModel?.items?.find(
      (item) => item.field === "isRequiredHandling"
    )?.value === "true";

    const unreadMessageFilterItem = filterModel?.items?.find(
      (item) => item.field === "unreadMessagesCount"
    )
    const isUnreadMessageFilterAvailable = unreadMessageFilterItem?.value == "0" &&  unreadMessageFilterItem?.operator == ">"
    const openStatuses = statusList.filter((status) => !status.closed).map((status) => status.value);
    const completedStatuses = statusList.filter((status) => status.closed).map((status) => status.value);
    

    const isInProgressFilterAvailable = status
      ? Array.isArray(status)
        ? status.includes(rrDbStatuses.IN_PROGRESS)
        : rrDbStatuses.IN_PROGRESS === status
      : undefined;

      const isCompletedFilterAvailable = status
      ? Array.isArray(status)
        ? completedStatuses.every((s) => status.includes(s)) 
        : undefined
      : undefined;

      const isOpenFilterAvailable = status
      ? Array.isArray(status)
        ? openStatuses.every((s) => status.includes(s))
        : undefined
      : undefined;

    const quickFilterItems = [{
      title: t("chat.messages"),
      onClick: handleUnreadMessageFilter,
      isSelected: isUnreadMessageFilterAvailable,
      icon: (props)=> <Chat  {...props}/>
    },
    {
      title: t("repairRequest.status.new"),
      onClick: handleNewRequestFilter,
      isSelected: isNewFilterAvailable,
      iconText: "N"
    },
    ...(isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE === false ? [{
      title: t("general.attention.label"),
      onClick: handleAttentionFilter,
      isSelected: isAttentionFilterAvailable,
      iconText: "!"
     }] : []),
    {
      title: t("general.inProcess"),
      onClick: handleInProgressRequestFilter,
      isSelected: isInProgressFilterAvailable,
      icon: (props)=> <Icon baseClassName="fas"   className="fa fa-spinner" {...props}/>

    },   
     ...(isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE === false ? [{
      title: t("general.overdue"),
      onClick: handleTooLateFilter,
      isSelected: isTooLate,
      icon: (props)=> <WatchLaterSharp {...props}/>
   }] : []),
    {
        title: t("repairRequest.quickfilter.complete"),
        onClick: handleCompletedRequestFilter,
        isSelected: isCompletedFilterAvailable,
        icon: (props) => <CheckCircleOutlineSharp {...props} />
    },
    {
        title: t("repairRequest.quickfilter.open"),
        onClick: handleOpenRequestFilter,
        isSelected: isOpenFilterAvailable,
        icon: (props) => <Icon baseClassName="fas" fontSize="small" className="fa-folder-open" {...props} />
    },
  ]
    const toolbarProps = {
      apiRef: apiRef,
      columns: columns,
      title: t("general.repairRequests"),
      totalRecords: totalRecords,
      getData: getRepairRequestsForPrintAndDownload,
      handleSearch: (value) => {
        handleSearch(value, (value) => {
          setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_SEARCH_TEXT[
            keyPrefix
            ],
            value
          );
        });
      },
      searchText: searchText,
      quickFilterItems: quickFilterItems,
      selectedRows:rowSelectionModel,
      taskMenuListItems:
        [...(!isHiddenResponsibleMenuItem({
          selectedItems: resolverIds,
          allBuildings: buildings,
          repairRequests: loadedRows,
        }) ? [{
          isLoading: organizationsLoading ,
          title: t("general.responsible"),
          onClick: (event) => {
            setResponsiblePopupPosition({
              x: event.clientX,
              y: event.clientY
            });
          },
          disabled: isRepairRequestsCompleted || rowSelectionModel?.ids?.size === 0
        },
          {
              title: t("general.button.complete"),
              icon: CheckCircleOutlineSharp,
              onClick: (event) => {
              setAnchorElCompleteTaskInfo(event.currentTarget);
            },
            disabled: isRepairRequestsCompleted
            },
      ] : [])]
      ,
      toolbarStartActions: app === apps.aftercare || app === apps.constructionQuality ? [
        {
          title: t("general.add"),
          icon: Add,
          onClick: () => setIsOpenObjectModal(true)
        }
      ] : [],
    };
    return toolbarProps;
  }, [rowSelectionModel,
    handleExportModal,
    filterModel,
    statusList,
    columns,
    handleSearch,
    isOpenObjectModal,
    searchText,
    handleTooLateFilter,
    handleAttentionFilter,
    handleNewRequestFilter,
    handleInProgressRequestFilter,
    handleCompletedRequestFilter,
    handleOpenRequestFilter,
    handleCheckboxChange,
    listToMapRedirectHandler,
    handleCellSort,
    totalRecords,
    organizationsLoading,
  ]);

  const initialState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          ...(getLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_VISIBILITY[keyPrefix], {})),
          ...getAfterCareRepairRequestHiddenColumns (t, !isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE).reduce((acc, column) => {
            return { ...acc, [column.field]: false, };
          }, {}),
        },
      },
    }),
    [getAfterCareRepairRequestHiddenColumns , t, isSiteManagerOrAfterCareEmployeeForBuildings]
  );

  const getTogglableColumns = (columns) => {
    const hiddenFields = getAfterCareRepairRequestHiddenColumns (t, !isSiteManagerOrAfterCareEmployeeForBuildings.NO_ONE)
    return columns.filter(column => !hiddenFields.some(hiddenFields => hiddenFields.field === column.field)).map((column) => column.field);
  };

  const slotProps = {
    columnsManagement: {
      getTogglableColumns,
    },
  }

  const onFilterModelChange = useCallback((newfilterModalModel) => {
    handleFilter(newfilterModalModel, (newfilterModalModel) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_FILTER[keyPrefix],
        newfilterModalModel
      );
    })
  }, [handleFilter])

  // First time loading we need to display page loadi  grid loading
  useEffect(() => {
    if (!loading) {
      isPageLoad.current = loading
    }
  }, [loading])


  const columnsForExport = (isAllDisplay = false) => {
    const hiddenFields = isAllDisplay ? [] : getAfterCareRepairRequestHiddenColumns(t, true)

    return columns.filter(column => !hiddenFields.some(hiddenFields => hiddenFields.field === column.field))
  }
  
  const handleCompleteTaskRepairRequest = async () => {
  try {
    const payload = {
      app:app,
      ids: rowSelectionModel?.type === "exclude" ? loadedRows?.filter(row => !rowSelectionModel.ids.has(row.requestId)).map((r)=>r.requestId) :Array.from(rowSelectionModel?.ids), 
      completionText: completionText,
      notes: notes
    };

    await completeRepairRequests(payload);
    await resetData()

    setAnchorElCompleteTaskInfo(null);
  } catch (error) {
    console.error(error);
  } 
};

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <DataGridV2
        mappingOfOldFieldToNewField={mappingOfOldFieldToNewField}
        columnsForExport={columnsForExport}
        MobileCustomRowComponent={(props) => {
          return <MobileCustomRow
            selectedCheckBoxs={Array.from(rowSelectionModel?.ids)}
            handleCheckboxChange={handleCheckboxChange}
            listToMapRedirectHandler={listToMapRedirectHandler}
            item={props.row}
            key={props.rowId}
          />
        }}
        apiRef={apiRef}
        customRowHeight= {isMobile ? 80 : 62}
        ref={muiTableRef}
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_SEARCH_TEXT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_ORDER[keyPrefix]}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_VISIBILITY[keyPrefix]}
        localDataGridViewIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_DATAGRID_VIEW[keyPrefix]}
        rowSelectionModel={rowSelectionModel}
        checkboxSelection
        columns={columns}
        toolbarProps={getToolbarProps}
        rows={loadedRows}
        filterMode="server"
        pagination
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={(paginationModal) =>
          onPaginationModelChange(paginationModal, (paginationModal) => {
            setLocalStorageItem(
              LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_PAGINATION[keyPrefix],
              paginationModal
            );
          })}
        paginationMode="server"
        loading={loading}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) =>
          handleSort(newSortModel, (newSortModal) => {
            setLocalStorageItem(
              LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_COLUMN_SORT[keyPrefix],
              newSortModal
            );
          })
        }
        onFilterModelChange={onFilterModelChange}
        initialState={initialState}
        slotProps={slotProps}
        {...(!!filterModel && { filterModel })}
      />
      <AddRepairRequest
        repairRequestCreateCallBack={resetData}
        isOpenObjectModal={isOpenObjectModal}
        setIsOpenObjectModal={(value) => setIsOpenObjectModal(value)}
      />
      <RepairRequestCompleteTaskPopover
        open={Boolean(anchorElCompleteTaskInfo)} 
        anchorEl={anchorElCompleteTaskInfo}
        onClose={() =>
          flushSync(() => setAnchorElCompleteTaskInfo(null))
        }
        handleType="complete"
        textWarning={t('repairRequest.action.completionTextWarning')}
        textBoxLabel={t('workOrders.solution')}
        from={'repairRequestGrid'}
        textBoxValue={completionText}
        onTextBoxChange={(e) =>
          flushSync(() => setCompletionText(e.target.value))
        }
        notesToEdit={notes}
        onNotesChange={(e) =>
          flushSync(() => setNotes(e.target.value))
        }
        onSubmit={handleCompleteTaskRepairRequest}
      />

      <SelectResponsiblePerson
        isSubmitting={isSubmitting}
        anchorOrigin={{
          vertical: responsiblePopupPosition?.y,
          horizontal: responsiblePopupPosition?.x,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        customTitle={t("general.responsible")}
        onSubmit={async (values, setSelectResponsiblePersonState) => {
          await handleResponsiblePerson(values)
          setSelectResponsiblePersonState?.((prevState) => ({
            ...prevState,
            relationId: ""
          }))
        }
        }
        setLoading={(val) => {
          flushSync(() => {
            setOrganizationsLoading(val);
            setIsSubmitting(true)
          })
        }}
        loading={organizationsLoading}
        forRepairRequests
        repairRequestIds={resolverIds}
        anchorEl={responsiblePopupPosition.x}
        onClose={() => {
          resetResponsiblePopupPosition()
        }}
      />
      {buyerContactInfo.anchorEl &&
        <BuyerContactPopover
          loading={buyerContactInfo.loading}
          data={buyerContactInfo.data}
          anchorEl={buyerContactInfo.anchorEl}
          onClose={handleCloseBuyerContactInfo}
        />
      }
      <Snackbar
        open={toast?.open}
        autoHideDuration={6000}
        onClose={() =>
          setToast({
            open: false,
            message: "",
            type: "error"
          })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        key={toast?.message}
      >
        <Alert
          onClose={() =>
            setToast({
              open: false,
              message: "",
              type: "error"
            })
          }
          severity={toast?.type}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "hidden ",
    padding: 0,
  },
}));

export default AfterCareRepairRequests